import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from 'react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import * as S from './styles';
import { CorporateExpensesTheme } from '../../styles/CorporateExpensesTheme';
import CompanyForm, { CompanyFormSchemaPreRegister } from './Forms/CompanyForm/index';
import ContactForm, { ContactFormSchema } from './Forms/ContactForm';
import Success from '../../components/Success';
import ErrorModal from '../../components/ui/ErrorModal';
import { Button } from '../../components/ui/Button';
import { PreRegisterType, UtmParams } from '../../@types';
import { CreatePreRegister } from '../../services/queries/PreRegister';
import { parseMaskedCnpjToRaw, parseMaskedPhoneToRaw } from '../../utils/masks';
import { getUtmParams } from '../../utils/analytics';
import { RiArrowRightSLine, RiWhatsappLine } from 'react-icons/ri';
import Modal from '../../components/ui/Modal';
import VideoModalForm from './Forms/VideoModalForm';
import VideoModal from './Forms/components/VideoModal';

import { errorToMessage } from '../../utils/errorMessages';
import BountyLogo from '../../assets/Bounty-logo-white.svg';

const INITIAL_DATA: PreRegisterType = {
	name: '',
	business_type: '',
	number_of_collaborators: '',
	contact: {
		full_name: '',
		email: '',
		phone: '',
		position_in_the_company: '',
	},
	utm:  {} as UtmParams
};

const Information = [
	{
		title: 'Dados da empresa',
		description: 'Preencha os dados abaixo para iniciar o seu cadastro.',
		form: <CompanyForm />,
	},
	{
		title: 'Dados do contato',
		description: null,
		form: <ContactForm />,
	},
];

const schemas = [CompanyFormSchemaPreRegister, ContactFormSchema];

export default function PreRegister() {
	const [isFormVideoSubmitted, setFormVideoSubmitted] = useState(false);
	const [isOpen, setOpen] = useState(false);
	const [currentStep, setCurrentStep] = useState(0);
	const isLastStep = currentStep === 1;
	const [formData, setFormData] = useState({});
	const [formSubmitted, setFormSubmitted] = useState(false);
	const form = useForm({
		resolver: zodResolver(schemas[currentStep]),
		defaultValues: INITIAL_DATA,
	});
	const { search } = useLocation();
	const [errorMessage, setErrorMessage] = useState<{title: string, message: string} | null>(null);

	const { handleSubmit } = form;

	const createPreRegiserMutation = useMutation(
		(data: PreRegisterType) => CreatePreRegister(data),
		{
			onSuccess: () => {
				setFormSubmitted(true);
			},
			onError: (error) => {
				setErrorMessage(errorToMessage(error as Error, {
					title: 'Erro ao criar pré-cadastro',
					message: 'Não foi possível criar o pré-cadastro, tente novamente mais tarde.'})
				);
			},
		}
	);

	const onSubmit = (data: PreRegisterType) => {
		const finalData = { ...formData, ...data };
		finalData.contact.phone = parseMaskedPhoneToRaw("+55" + finalData.contact.phone);
		finalData.utm = getUtmParams(new URLSearchParams(search)) as UtmParams
		if (Object.keys(finalData.utm).length === 0) {
			finalData.utm = undefined;
		}
		createPreRegiserMutation.mutate(finalData);
	};

	const next = async () => {
		if (await form.trigger()) {
			const currentFormData = form.getValues();
			setFormData((prev) => ({ ...prev, ...currentFormData }));
			setCurrentStep((current) => current + 1);
		}
	};

	const back = () => setCurrentStep((current) => current - 1);

	const handleCallSpecialist = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault()
		window.open('https://calendly.com/iris-souza-bounty/agenda?back=1', '_blank')
	};

	const handleWatchDemo = () => {
		setOpen(true)
	}

	const RequestClose = () => {
		setOpen(false);
	};

	const handleFormVideoSubmit = () => {
        setFormVideoSubmitted(true);
    };
	
	return (
		<FormProvider {...form}>
			<ErrorModal errorMessage={errorMessage} resetMessage={() => setErrorMessage(null)} />
			<S.Container>
				<img src={BountyLogo} alt='Bounty Logo' />
				<CorporateExpensesTheme />
				<S.LeftContainer>
					<div>
						<div className='box1'>
							<h2>Quer assistir uma <p>demonstração?</p></h2>
							<button
								onClick={handleWatchDemo}>
									Acesse agora 
									<RiArrowRightSLine size={24} />
							</button>
							<Modal
								onRequestClose={RequestClose}
								isOpen={isOpen}
								position="center"
								showClose={false}
								>
							{isFormVideoSubmitted ? (
                                    <VideoModal />
                                ) : (
                                    <VideoModalForm 
										onSubmit={handleFormVideoSubmit}
										onRequestClose={RequestClose} 
									/>   
                            )}
							</Modal>							
						</div>
						<div className='box2'>
							<p>Ficou com dúvida?</p>
							<a href="https://calendly.com/iris-souza-bounty/agenda?back=1" target='_blank' rel="noreferrer">
									Fale com um especialista
							</a>
						</div>
					</div>
				</S.LeftContainer>

				<S.RightContainer>
					<S.MainContainer>
						{formSubmitted ? (
							<Success
								title='Seus dados foram recebidos'
								message='Entraremos em contato pelo e-mail cadastrado em'
								messageTime=' até um dia útil.'
							/>
						) : (
							<>
								{Information[currentStep].description && (
									<S.Description>
										{Information[currentStep].description}
									</S.Description>
								)}
								<S.Header>
									<h2>{Information[currentStep].title}</h2>
									{currentStep < 2 && (
										<>
											<p>Todos os campos são obrigatórios</p>
											<div className='progress-bar'>
												<div
													className={`progress-step step-${currentStep + 1}`}
												/>
											</div>
										</>
									)}
								</S.Header>
								<form onSubmit={handleSubmit(onSubmit)}>
									{Information[currentStep].form}
									{!isLastStep ? (
										<S.BottomOptionsContainer>
											<Button
												$outline
												intent='terciary'
												roundness='lg'
												onClick={(e) => handleCallSpecialist(e)}
											>
												Falar com um especialista
											</Button>
											<Button onClick={next} roundness='lg'>
												Próximo
											</Button>
										</S.BottomOptionsContainer>
									) : (
										<S.BottomOptionsContainer>
											<Button
												$outline
												intent='terciary'
												roundness='lg'
												onClick={back}
											>
												Voltar
											</Button>
											<Button
												roundness='lg'
												type='submit'
												disabled={createPreRegiserMutation.isLoading}
											>
												Concluir
											</Button>						
										</S.BottomOptionsContainer>
									)}

									{currentStep === 1 && (
										<S.SupportMessage>
											Ao clicar em {<span>Concluir</span>}, você confirma que os
											dados são verdadeiros e podem ser verificados.
										</S.SupportMessage>
									)}
								</form>
							</>
						)}
					</S.MainContainer>
				</S.RightContainer>
			</S.Container>
		</FormProvider>
	);
}
